import * as d3 from 'd3';
import { Block } from '../../types';
import { getBlockDimensions } from '../../utils/blockUtils';

interface BlockSelectionMenuCallbacks {
  onDelete?: (block: Block) => void;
  onAddLine?: (block: Block) => void;
  onToggleLayers?: () => void;
  onFetchInfo?: (block: Block) => void;
  /** Override hover title for the info button (e.g. unavailable hint). */
  infoButtonTitle?: string;
  onLinkTask?: (block: Block) => void;
  onUnlinkTask?: (block: Block) => void;
  onLinkAttributes?: (block: Block) => void;
  onUnlinkAttributes?: (block: Block) => void;
  onAttachTextAnnotation?: (block: Block) => void;
}

export function createBlockSelectionMenu(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  block: Block,
  callbacks: BlockSelectionMenuCallbacks = {},
  t: (key: string) => string

): d3.Selection<HTMLDivElement, unknown, null, undefined> {
  // Remove any existing menus
  container.selectAll(`[class^="block-selection-menu"]`).remove();

  const { width } = getBlockDimensions(block);

  // Calculate menu position
  const menuX = (block.x || 0) + width / 2;
  const menuY = (block.y || 0) - 40;

  const menu = container
    .append('div')
    .attr('class', `block-selection-menu-${block.id}`)
    .style('position', 'absolute')
    .style('left', `${menuX}px`)
    .style('top', `${menuY}px`)
    .style('transform', 'translateX(-50%)')
    .style('background-color', 'white')
    .style('border', '1px solid #e2e8f0')
    .style('border-radius', '6px')
    .style('padding', '4px')
    .style('display', 'flex')
    .style('flex-direction', 'row')
    .style('flex-wrap', 'nowrap')
    .style('align-items', 'center')
    .style('width', 'max-content')
    .style('white-space', 'nowrap')
    .style('gap', '4px')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
    .style('z-index', '5'); // Réduit pour être sous le menu des layers

  // Info button (previously line button)
  if (callbacks.onFetchInfo) {
    const infoButton = menu
      .append('button')
      .style('background-color', '#e0f2fe')
      .style('color', '#0284c7')
      .style('padding', '6px')
      .style('border-radius', '4px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .style('border', 'none')
      .attr('title', callbacks.infoButtonTitle || t('features.swimlane.verifInfo'))
      .style('transition', 'all 0.2s')
      .on('mouseenter', function () {
        d3.select(this).style('background-color', '#bae6fd');
      })
      .on('mouseleave', function () {
        d3.select(this).style('background-color', '#e0f2fe');
      })
      .on('click', (event) => {
        event.stopPropagation();
        callbacks.onFetchInfo?.(block);
      });

    // Info icon (i)
    infoButton
      .append('svg')
      .attr('width', '16')
      .attr('height', '16')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', '2')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .html('<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>');
  }

  // Layers button
  if (callbacks.onToggleLayers) {
    const layersButton = menu
      .append('button')
      .style('background-color', '#f0fdf4')
      .style('color', '#16a34a')
      .style('padding', '6px')
      .style('border-radius', '4px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .attr('title', t('features.swimlane.showLayers'))
      .style('border', 'none')
      .style('transition', 'all 0.2s')
      .on('mouseenter', function () {
        d3.select(this).style('background-color', '#dcfce7');
      })
      .on('mouseleave', function () {
        d3.select(this).style('background-color', '#f0fdf4');
      })
      .on('click', (event) => {
        event.stopPropagation();
        callbacks.onToggleLayers?.();
      });

    // Layers icon
    layersButton
      .append('svg')
      .attr('width', '16')
      .attr('height', '16')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', '2')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .html('<path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/>');
  }

  // Delete button
  if (callbacks.onDelete) {
    const deleteButton = menu
      .append('button')
      .style('background-color', '#fee2e2')
      .style('color', '#dc2626')
      .style('padding', '6px')
      .style('border-radius', '4px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .attr('title', t('words.delete'))
      .style('border', 'none')
      .style('transition', 'all 0.2s')
      .on('mouseenter', function () {
        d3.select(this).style('background-color', '#fecaca');
      })
      .on('mouseleave', function () {
        d3.select(this).style('background-color', '#fee2e2');
      })
      .on('mousedown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        callbacks.onDelete?.(block);
      })
      .on('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });

    // Trash icon
    deleteButton
      .append('svg')
      .attr('width', '16')
      .attr('height', '16')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', '2')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .html('<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>');
  }

  // Note: link/unlink task button (sticky notes only — not BPMN/DMN/VSM artifacts)
  const isStickyNote = block.type === 'note' && !block.bpmnSymbol && !block.dmnSymbol && !block.vsmSymbol;
  if (isStickyNote && (callbacks.onLinkTask || callbacks.onUnlinkTask)) {
    const isLinked = Boolean((block as any).linkedTaskId);
    const taskButton = menu
      .append('button')
      .style('background-color', isLinked ? '#fee2e2' : '#eff6ff')
      .style('color', isLinked ? '#dc2626' : '#1d4ed8')
      .style('padding', '6px')
      .style('border-radius', '4px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .attr('title', isLinked ? (t('words.unlinkTask') || 'Unlink task') : (t('words.linkTask') || 'Link to task'))
      .style('border', 'none')
      .style('transition', 'all 0.2s')
      .on('mouseenter', function () {
        d3.select(this).style('background-color', isLinked ? '#fecaca' : '#dbeafe');
      })
      .on('mouseleave', function () {
        d3.select(this).style('background-color', isLinked ? '#fee2e2' : '#eff6ff');
      })
      .on('click', (event) => {
        event.stopPropagation();
        if (isLinked) {
          callbacks.onUnlinkTask?.(block);
        } else {
          callbacks.onLinkTask?.(block);
        }
      });

    taskButton
      .append('svg')
      .attr('width', '16')
      .attr('height', '16')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', '2')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .html(isLinked
        ? '<path d="m10 13-2 2a3 3 0 1 1-4-4l2-2"/><path d="m14 11 2-2a3 3 0 1 1 4 4l-2 2"/><path d="m3 3 18 18"/>'
        : '<path d="m10 13-2 2a3 3 0 1 1-4-4l2-2"/><path d="m14 11 2-2a3 3 0 1 1 4 4l-2 2"/><path d="M8 12h8"/>');
  }

  // Note: link/unlink attributes button (sticky notes only)
  if (isStickyNote && (callbacks.onLinkAttributes || callbacks.onUnlinkAttributes)) {
    const linkedAttrIds = Array.isArray((block as any).linkedAttributeIds) ? (block as any).linkedAttributeIds : [];
    const hasLinkedAttributes = linkedAttrIds.length > 0;
    const hasLinkedTask = Boolean((block as any).linkedTaskId);
    const attributeButton = menu
      .append('button')
      .style('background-color', !hasLinkedTask ? '#f3f4f6' : (hasLinkedAttributes ? '#fee2e2' : '#f5f3ff'))
      .style('color', !hasLinkedTask ? '#9ca3af' : (hasLinkedAttributes ? '#dc2626' : '#7c3aed'))
      .style('padding', '6px')
      .style('border-radius', '4px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', hasLinkedTask ? 'pointer' : 'not-allowed')
      .attr('title', hasLinkedTask
        ? (hasLinkedAttributes ? (t('words.unlinkAttribute') || 'Unlink attribute') : (t('words.linkAttribute') || 'Link to attribute'))
        : (t('words.linkTaskFirst') || 'Link task first'))
      .style('border', 'none')
      .style('transition', 'all 0.2s')
      .on('mouseenter', function () {
        d3.select(this).style('background-color', !hasLinkedTask ? '#f3f4f6' : (hasLinkedAttributes ? '#fecaca' : '#ede9fe'));
      })
      .on('mouseleave', function () {
        d3.select(this).style('background-color', !hasLinkedTask ? '#f3f4f6' : (hasLinkedAttributes ? '#fee2e2' : '#f5f3ff'));
      })
      .on('click', (event) => {
        event.stopPropagation();
        if (!hasLinkedTask) return;
        if (hasLinkedAttributes) {
          callbacks.onUnlinkAttributes?.(block);
        } else {
          callbacks.onLinkAttributes?.(block);
        }
      });

    // Tag icon
    attributeButton
      .append('svg')
      .attr('width', '16')
      .attr('height', '16')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', '2')
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .html(hasLinkedAttributes
        ? '<path d="M20 10V6a2 2 0 0 0-2-2h-4"/><path d="M4 14v4a2 2 0 0 0 2 2h4"/><path d="M14 10l-4 4"/><path d="M3 3l18 18"/>'
        : '<path d="M20 10V6a2 2 0 0 0-2-2h-4"/><path d="M4 14v4a2 2 0 0 0 2 2h4"/><path d="M14 10l-4 4"/>');
  }

  // Attach BPMN text annotation (bracket + text) with a dotted association line
  // if (block.type !== 'note' && callbacks.onAttachTextAnnotation) {
  //   const annotationButton = menu
  //     .append('button')
  //     .style('background-color', '#f8fafc')
  //     .style('color', '#334155')
  //     .style('padding', '6px')
  //     .style('border-radius', '4px')
  //     .style('display', 'flex')
  //     .style('align-items', 'center')
  //     .style('justify-content', 'center')
  //     .style('cursor', 'pointer')
  //     .attr('title', t('features.swimlane.addTextAnnotation') || 'Add text annotation')
  //     .style('border', 'none')
  //     .style('transition', 'all 0.2s')
  //     .on('mouseenter', function () {
  //       d3.select(this).style('background-color', '#e2e8f0');
  //     })
  //     .on('mouseleave', function () {
  //       d3.select(this).style('background-color', '#f8fafc');
  //     })
  //     .on('click', (event) => {
  //       event.stopPropagation();
  //       callbacks.onAttachTextAnnotation?.(block);
  //     });

  //   annotationButton
  //     .append('svg')
  //     .attr('width', '16')
  //     .attr('height', '16')
  //     .attr('viewBox', '0 0 24 24')
  //     .attr('fill', 'none')
  //     .attr('stroke', 'currentColor')
  //     .attr('stroke-width', '2')
  //     .attr('stroke-linecap', 'round')
  //     .attr('stroke-linejoin', 'round')
  //     .html('<path d="M4 4v16"/><path d="M4 4h6"/><path d="M4 20h6"/><path d="M12 8h8"/><path d="M12 12h6"/><path d="M12 16h4"/>');
  // }

  // Prevent menu from closing when clicking inside
  menu.on('click', (event) => {
    event.stopPropagation();
  });

  return menu;
}